import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminNav from "../../components/admin/AdminNav";
import { GroupContext } from "../../context/GroupContext";
import api from "../../utils/api";
import ConfirmationModal from '../../components/common/ConfirmationModal'; // Adjust path if needed
import InfoModal from '../../components/common/InfoModal';
import { NAME_LIMITS } from '../../utils/nameLimits';
import { animalLabel } from '../../utils/animals';

import { stateLabel } from '../../utils/usStates';

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];
const PORTION_OPTIONS = ["Full", "Half", "Quarter"];
const EPSILON = 0.001;

export default function EditGroup() {
  const { t } = useTranslation();
  // Read by the load effect, which must not re-run when the language changes
  const tRef = useRef(t);
  tRef.current = t;
  const { id } = useParams();
  const navigate = useNavigate();
  const { replaceGroup, getGroup, fetchGroup, groups: contextGroups } = useContext(GroupContext);

  const currentUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u;
    } catch {
      return {};
    }
  })();
  const role = (currentUser?.role || "").toLowerCase();
  const isSupplierAdmin = role === "supplieradmin";
  // Comma-joined so effects depend on a string, not a new array each render
  const scopedSupplierKey = (currentUser?.supplierIds || []).join(',');

  const [group, setGroup] = useState({
    name: "",
    supplierId: "",
    state: "",
    animal: "",
    visibility: "public",
    shortDescription: "",
    cashPayment: { enabled: false, deadlineHours: "" },
    costPerPortion: "",
    totalCost: "",
    remainingCost: "",
    portionsAvailable: [],
    status: "open",
    dateClosed: null,
    updatedAt: null,
  });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const lastSyncedRef = useRef(null);

  // Info Modal state for results
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  // Confirmation Modal state for update confirmation
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const effectiveStatus = (() => {
    const rem = Number(group.remainingCost ?? 0);
    if (group.status === "closed") return "closed";
    if (rem <= EPSILON) return "full";
    return "open";
  })();

  // --- Load data and sync omitted for brevity (same as your current code) ---
  // Copy the useEffect loading logic and syncing exactly as you have it
  // I'll skip pasting here for brevity, you can keep yours unchanged

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const res = await api.get(`/api/suppliers`);
        let supplierList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.suppliers)
          ? res.data.suppliers
          : [];

        if (isSupplierAdmin && scopedSupplierKey) {
          supplierList = supplierList.filter((s) =>
            scopedSupplierKey.split(',').includes(String(s._id))
          );
        }
        setSuppliers(supplierList);

        if (supplierList.length === 0) {
          toast.warn(tRef.current('editGroup.noSuppliers'));
        }

        let contextGroup = getGroup ? getGroup(id) : null;
        if (!contextGroup) {
          contextGroup = fetchGroup ? await fetchGroup(id) : await api.get(`/api/groups/${id}`).then(r => r.data.group || r.data);
        }

        if (contextGroup) {
          const groupSupplierId =
            typeof contextGroup.supplierId === "string"
              ? contextGroup.supplierId
              : contextGroup.supplierId?._id;

          if (
            isSupplierAdmin &&
            scopedSupplierKey &&
            !scopedSupplierKey.split(',').includes(String(groupSupplierId))
          ) {
            toast.error(tRef.current('editGroup.noPermission'));
            navigate(-1);
            return;
          }

          setGroup({
            name: contextGroup.name || "",
            supplierId: groupSupplierId || "",
            state: contextGroup.state || "",
            animal: contextGroup.animal || "",
            visibility: contextGroup.visibility || "public",
            shortDescription: contextGroup.shortDescription || "",
            cashPayment: {
              enabled: !!contextGroup.cashPayment?.enabled,
              deadlineHours: contextGroup.cashPayment?.deadlineHours ?? "",
            },
            costPerPortion: contextGroup.costPerPortion || "",
            totalCost: contextGroup.totalCost || "",
            remainingCost:
              contextGroup.remainingCost != null
                ? contextGroup.remainingCost
                : contextGroup.totalCost || "",
            portionsAvailable: contextGroup.portionsAvailable || [],
            status: contextGroup.status || "open",
            dateClosed: contextGroup.dateClosed || null,
            updatedAt: contextGroup.updatedAt || null,
          });
          lastSyncedRef.current = contextGroup.updatedAt;
        } else {
          toast.error(tRef.current('editGroup.notFound'));
          navigate(-1);
        }
      } catch (err) {
        console.error(err);
        toast.error(tRef.current('editGroup.loadFailed'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, contextGroups, fetchGroup, getGroup, isSupplierAdmin, scopedSupplierKey, navigate]);

  useEffect(() => {
    if (!getGroup) return;
    const contextGroup = getGroup(id);
    if (!contextGroup) return;
    if (contextGroup.updatedAt && contextGroup.updatedAt !== lastSyncedRef.current) {
      setGroup({
        name: contextGroup.name || "",
        supplierId:
          typeof contextGroup.supplierId === "string"
            ? contextGroup.supplierId
            : contextGroup.supplierId?._id || "",
        state: contextGroup.state || "",
        animal: contextGroup.animal || "",
        costPerPortion: contextGroup.costPerPortion || "",
        totalCost: contextGroup.totalCost || "",
        remainingCost:
          contextGroup.remainingCost != null
            ? contextGroup.remainingCost
            : contextGroup.totalCost || "",
        portionsAvailable: contextGroup.portionsAvailable || [],
        status: contextGroup.status || "open",
        dateClosed: contextGroup.dateClosed || null,
        updatedAt: contextGroup.updatedAt || null,
      });
      lastSyncedRef.current = contextGroup.updatedAt;
    }
  }, [getGroup, id, contextGroups]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "costPerPortion") {
      const remaining = parseFloat(group.remainingCost);
      if (!isNaN(remaining) && parseFloat(value) > remaining) {
        toast.error(t('editGroup.costExceeds'));
        return setGroup((prev) => ({ ...prev, [name]: remaining.toString() }));
      }
    }

    if (name === "remainingCost") {
      const cost = parseFloat(group.costPerPortion);
      if (!isNaN(cost) && parseFloat(value) < cost) {
        toast.error(t('editGroup.remainingLess'));
      }
    }

    if (name === "status") {
      const newStatus = value;
      setGroup((prev) => ({
        ...prev,
        status: newStatus,
        remainingCost:
          newStatus === "full" || newStatus === "closed" ? "0" : prev.remainingCost,
        dateClosed: newStatus === "closed" ? new Date().toISOString() : prev.dateClosed,
      }));
      return;
    }

    setGroup((prev) => ({ ...prev, [name]: value }));
  };

  const togglePortion = (portion) => {
    setGroup((prev) => {
      const selected = prev.portionsAvailable.includes(portion);
      return {
        ...prev,
        portionsAvailable: selected
          ? prev.portionsAvailable.filter((p) => p !== portion)
          : [...prev.portionsAvailable, portion],
      };
    });
  };

  // This replaces handleSubmit on form submit:
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setConfirmModalOpen(true); // open confirm modal before actually submitting
  };

  // Confirmed submit handler
  const handleConfirmSubmit = async () => {
    setConfirmModalOpen(false);

    const numericRemaining = parseFloat(group.remainingCost);
    const numericTotalCost = parseFloat(group.totalCost);

    if (numericRemaining > numericTotalCost) {
      setInfoModal({
        isOpen: true,
        title: t('editGroup.validationError'),
        message: t('editGroup.remainingExceeds'),
      });
      return;
    }

    setSubmitting(true);
    try {
      // The deadline is sent as a whole number of hours only when cash is on
      const payload = {
        ...group,
        cashPayment: {
          enabled: group.cashPayment.enabled,
          deadlineHours: group.cashPayment.enabled ? Number(group.cashPayment.deadlineHours) : null,
        },
      };
      const response = await api.put(`/api/groups/${id}`, payload);
      const updatedGroup = response.data.group || response.data;

      replaceGroup(updatedGroup);

      setGroup((prev) => ({
        ...prev,
        ...updatedGroup,
        remainingCost:
          updatedGroup.remainingCost != null
            ? updatedGroup.remainingCost
            : updatedGroup.totalCost || "",
      }));

      setInfoModal({
        isOpen: true,
        title: t('createGroup.titleSuccess'),
        message: t('editGroup.updated', { name: updatedGroup.name }),
      });

      setTimeout(() => {
        setInfoModal({ isOpen: false, title: "", message: "" });
        navigate(-1);
      }, 2000);
    } catch (err) {
      console.error(err);
      setInfoModal({
        isOpen: true,
        title: t('messages.titleError'),
        message: t('editGroup.updateFailed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <p className="text-center mt-10 text-gray-600">{t('editGroup.loading')}</p>
    );

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-md mx-auto mt-10 p-6 bg-white shadow-md rounded">
        <h1 className="text-2xl font-bold mb-6 text-center">{t('editGroup.title')}</h1>
        <form onSubmit={handleFormSubmit} className="space-y-5">
          {/* Form fields unchanged, same as your original */}

          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('createGroup.groupName')}</label>
            <input
              name="name"
              value={group.name}
              onChange={handleChange}
              maxLength={NAME_LIMITS.groupName.max}
              className="border p-2 w-full rounded"
              required
            />
          </div>

          {/* Short description (optional, plain text) */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('createGroup.shortDescription')}</label>
            <textarea
              name="shortDescription"
              value={group.shortDescription}
              onChange={handleChange}
              maxLength={NAME_LIMITS.shortDescription.max}
              rows={2}
              className="border p-2 w-full rounded"
            />
          </div>

          {/* Supplier */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('createGroup.supplier')}</label>
            <select
              name="supplierId"
              value={group.supplierId}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
              disabled={isSupplierAdmin}
              title={
                isSupplierAdmin
                  ? t('editGroup.supplierLocked')
                  : undefined
              }
            >
              <option value="">{t('createGroup.selectSupplier')}</option>
              {suppliers.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.displayName || s.name}
                </option>
              ))}
            </select>
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('createGroup.state')}</label>
            <select
              name="state"
              value={group.state}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            >
              <option value="">{t('createGroup.selectState')}</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {stateLabel(s)}
                </option>
              ))}
            </select>
          </div>

          {/* Animal */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('createGroup.animal')}</label>
            <select
              name="animal"
              value={group.animal}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            >
              <option value="">{t('createGroup.selectAnimal')}</option>
              <option value="Beef">{animalLabel(t, 'Beef')}</option>
              <option value="Lamb">{animalLabel(t, 'Lamb')}</option>
              <option value="Goat">{animalLabel(t, 'Goat')}</option>
              <option value="Sheep">{animalLabel(t, 'Sheep')}</option>
            </select>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('adminGroups.visibility')}</label>
            <select
              name="visibility"
              value={group.visibility}
              onChange={handleChange}
              className="border p-2 w-full rounded"
            >
              <option value="public">{t('createGroup.visPublic')}</option>
              <option value="private">{t('createGroup.visPrivate')}</option>
            </select>
          </div>

          {/* Cash payment: customers may pay the supplier in cash within the deadline */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={group.cashPayment.enabled}
                onChange={(e) => setGroup((prev) => ({ ...prev, cashPayment: { ...prev.cashPayment, enabled: e.target.checked } }))}
              />
              {t('cash.groupEnable')}
            </label>
            {group.cashPayment.enabled && (
              <div>
                <label htmlFor="cashDeadlineHours" className="block text-sm font-medium mb-1">{t('cash.deadlineHours')}</label>
                <input
                  id="cashDeadlineHours"
                  type="number"
                  min="1"
                  max="168"
                  step="1"
                  value={group.cashPayment.deadlineHours}
                  onChange={(e) => setGroup((prev) => ({ ...prev, cashPayment: { ...prev.cashPayment, deadlineHours: e.target.value } }))}
                  className="border p-2 w-full rounded"
                />
                <p className="text-xs text-gray-500 mt-1">{t('cash.deadlineHint')}</p>
              </div>
            )}
          </div>

          {/* Total Cost (readonly) */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('createGroup.totalCost')}</label>
            <input
              name="totalCost"
              value={group.totalCost}
              readOnly
              className="border p-2 w-full rounded bg-gray-100"
            />
          </div>

          {/* Remaining Cost */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('editGroup.remainingCost')}
            </label>
            <input
              name="remainingCost"
              type="number"
              value={group.remainingCost}
              onChange={handleChange}
              className="border p-2 w-full rounded"
              required
            />
          </div>

          {/* Cost Per Portion (readonly) */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('editGroup.costPerPortion')}
            </label>
            <input
              name="costPerPortion"
              type="number"
              value={group.costPerPortion}
              readOnly
              className="border p-2 w-full rounded bg-gray-100"
            />
          </div>

          {/* Portions Available toggle buttons */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('editGroup.portionsAvailable')}</label>
            <div className="flex gap-2">
              {PORTION_OPTIONS.map((portionOption) => {
                const selected = group.portionsAvailable.includes(portionOption);
                return (
                  <button
                    type="button"
                    key={portionOption}
                    onClick={() => togglePortion(portionOption)}
                    className={`px-3 py-1 rounded border text-sm ${
                      selected
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-gray-800 border-gray-400"
                    }`}
                  >
                    {t('portions.' + portionOption.toLowerCase())}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status (effective) */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('editGroup.statusEffective')}
            </label>
            <div className={`inline-block p-2 rounded ${getStatusClasses(effectiveStatus)}`}>
              {t('adminGroups.' + effectiveStatus)}
            </div>
          </div>

          {/* Status (stored) */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('editGroup.statusStored')}</label>
            <select
              name="status"
              value={group.status}
              onChange={handleChange}
              className={`p-2 w-full rounded ${getStatusClasses(group.status)}`}
            >
              <option value="open">{t('adminGroups.open')}</option>
              <option value="full">{t('adminGroups.full')}</option>
              <option value="closed">{t('adminGroups.closed')}</option>
            </select>
          </div>

          {/* Date Closed */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('viewGroup.dateClosed')}</label>
            <input
              type="text"
              value={group.dateClosed ? new Date(group.dateClosed).toLocaleString() : ""}
              readOnly
              className="border p-2 w-full rounded bg-gray-100"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              disabled={submitting}
            >
              {t('profile.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              {submitting ? t('editGroup.updating') : t('editGroup.update')}
            </button>
          </div>
        </form>

        <ToastContainer position="top-right" autoClose={3000} theme="colored" />

        {/* Info Modal for success/errors */}
        <InfoModal
          isOpen={infoModal.isOpen}
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ isOpen: false, title: "", message: "" })}
        />

        {/* Confirmation Modal for update confirmation */}
        <ConfirmationModal
          isOpen={confirmModalOpen}
          title={t('editGroup.confirmTitle')}
          message={t('editGroup.confirmMessage')}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('profile.cancel')}
          onConfirm={handleConfirmSubmit}
          onClose={() => setConfirmModalOpen(false)}
        />
      </div>
    </div>
  );
}

function getStatusClasses(status) {
  switch (status) {
    case "open":
      return "bg-green-100 text-green-800 border border-green-300";
    case "full":
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    case "closed":
      return "bg-gray-200 text-gray-800 border border-gray-300";
    default:
      return "";
  }
}